import React, { useState, useRef, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import Drawer from 'components/Drawer'
import Button from 'components/Button'
import { FONT } from 'theme/font'
import { message, Form, Table, Input } from 'antd'
import { Select as AntSelect } from 'antd'
import * as XLSX from 'xlsx'

const FormItem = styled(Form.Item)`
  margin-bottom: 16px;
`
const UploadHeading = styled.h3`
  font-size: ${FONT.F_LG};
  font-weight: 500;
  color: #111827;
  margin-top: 20px;
  margin-bottom: 12px;
`
const StyledAntSelect = styled(AntSelect)`
  width: 100%;

  .ant-select-selector {
    border-radius: 8px !important;
    padding: 4px 12px !important;
    min-height: 40px !important;
  }

  .ant-select-selection-search-input {
    height: 38px !important;
  }

  .ant-select-selection-placeholder {
    color: #667085 !important;
    line-height: 32px !important;
  }
`

const UploadArea = styled.div`
  background: #f9fafb;
  border: 1px dashed #d0d5dd;
  border-radius: 12px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 20px;

  &:hover {
    background: #f5f8ff;
    border-color: #444ce7;
  }
`

const UploadIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #444ce7;
`

const UploadText = styled.div`
  font-size: ${FONT.F_SM};
  color: #101828;
  text-align: center;
`

const FormatText = styled.div`
  font-size: ${FONT.F_XS};
  color: #475467;
`

const DrawerFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`

const FileContainer = styled.div`
  margin-top: 16px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
`

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const FileName = styled.span`
  font-size: ${FONT.F_SM};
  color: #111827;
`

const FileIcon = styled.div`
  color: #6b7280;
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: ${FONT.F_SM};
  padding: 4px 8px;

  &:hover {
    background: #fee2e2;
    border-radius: 4px;
  }
`

const UploadedFileSection = styled.div`
  margin-top: 24px;
  padding: 16px;
  background: #f0fdf4;
  border-radius: 8px;
`

const UploadedFileHeader = styled.div`
  font-size: ${FONT.F_SM};
  color: #15803d;
  margin-bottom: 8px;
`

const TableContainer = styled.div`
  margin-top: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;

  .ant-table-wrapper {
    border-radius: 8px;
  }

  .ant-table-thead > tr > th {
    background-color: #f9fafb;
    color: #344054;
    font-weight: 500;
  }
`

const TableTitle = styled.div`
  font-weight: 500;
  font-size: ${FONT.F_LG};
  color: #101828;
  margin-bottom: 16px;
  margin-top: 24px;
`

const ACCEPTED_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
]

const MAX_SIZE = 5 * 1024 * 1024 // 5MB

const AddManuallyButton = styled.button`
  background: #f5f8ff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: ${FONT.F_SM};
  color: #444ce7;
  cursor: pointer;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: #e0e7ff;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ManualEntrySection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`

const ManualEntryGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
`

// Add this new styled component to reduce margin in form items
const StyledFormItem = styled(Form.Item)`
  margin-bottom: 4px;
`

const AddButton = styled.button`
  background: #444ce7;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: ${FONT.F_SM};
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;

  &:hover {
    background: #3538cd;
  }

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }
`

const YearSelectContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;

  > div {
    flex: 1;
  }
`

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #fee2e2;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const SkippedRecordsContainer = styled.div`
  margin-top: 24px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  overflow: hidden;

  .ant-table-wrapper {
    border-radius: 8px;
  }

  .ant-table-thead > tr > th {
    background-color: #fee2e2;
    color: #b91c1c;
    font-weight: 500;
  }
`

const SkippedRecordsTitle = styled.div`
  font-weight: 500;
  font-size: ${FONT.F_LG};
  color: #b91c1c;
  margin-bottom: 16px;
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const WarningIcon = styled.span`
  color: #b91c1c;
  display: inline-flex;
`

const BulkUploadDrawer = ({
  visible,
  onClose,
  assessmentData,
  checkedData,
  entityType = 'college',
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)

  const [skippedRecords, setSkippedRecords] = useState([])

  const [uploadedFile, setUploadedFile] = useState(() => {
    const saved = localStorage.getItem('uploadedFile')
    return saved ? JSON.parse(saved) : null
  })
  const fileInputRef = useRef(null)
  const [form] = Form.useForm()

  // State for degrees, departments, and specializations
  const [degrees, setDegrees] = useState([])
  const [departments, setDepartments] = useState([])
  const [specializations, setSpecializations] = useState([])

  // State for excel data
  const [excelData, setExcelData] = useState([])
  const [excelColumns, setExcelColumns] = useState([])

  // Selected values
  const [selectedDegree, setSelectedDegree] = useState(null)
  const [selectedDepartment, setSelectedDepartment] = useState(null)
  const [selectedSpecialization, setSelectedSpecialization] = useState(null)

  // New state for manual entry
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [manualFirstName, setManualFirstName] = useState('')
  const [manualLastName, setManualLastName] = useState('')
  const [manualEmail, setManualEmail] = useState('')
  const [manualEntryError, setManualEntryError] = useState('')
  const [passingYear, setPassingYear] = useState(null)

  // Load degrees on component mount
  useEffect(() => {
    if (visible && assessmentData?.qualificationData) {
      // //console.log('Qualification data:', assessmentData.qualificationData)
      setDegrees(assessmentData.qualificationData || [])
    }
  }, [visible, assessmentData])

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let year = currentYear - 5; year <= currentYear + 10; year++) {
      years.push({ label: year.toString(), value: year })
    }
    return years
  }, [])

  const handlePassingYearChange = value => {
    // //console.log('Setting passing year:', value)
    setPassingYear(value)
    // Sync with form
    form.setFieldsValue({ passingYear: value })
  }

  const handleDegreeChange = value => {
    const degree = degrees.find(d => d.degreeId === value)
    setSelectedDegree(degree)
    setDepartments(degree?.streams || [])
    setSelectedDepartment(null)
    setSpecializations([])
    setSelectedSpecialization(null)
    form.setFieldsValue({ department: undefined, specialization: undefined })
  }

  const handleDepartmentChange = value => {
    const department = departments.find(d => d.streamId === value)
    setSelectedDepartment(department)
    // //console.log('Selected department:', department,value)
    setSpecializations(department?.specialisations || [])
    setSelectedSpecialization(null)
    form.setFieldsValue({ specialization: undefined })
  }

  const handleSpecializationChange = value => {
    const specialization = specializations.find(
      s => s.specialisationId === value
    )
    setSelectedSpecialization(specialization)
  }

  // Add this helper above parseExcelFile
  const isValidEmail = email => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(String(email).toLowerCase())
  }

  const parseExcelFile = file => {
    // Clear any existing data completely
    setExcelData([])
    setExcelColumns([])
    setSkippedRecords([])

    const reader = new FileReader()
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })

        // Get first worksheet
        const worksheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[worksheetName]

        // Convert to JSON with headers
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
          blankrows: false,
        })

        if (jsonData.length > 0) {
          // First row contains headers
          const headers = jsonData[0].map(h => h.toLowerCase()) // Normalize header names

          // Create columns for the table
          const tableColumns = headers.map((header, index) => ({
            title: header,
            dataIndex: header,
            key: header,
            render: text => (text !== undefined && text !== '' ? text : '-'),
          }))

          // Add delete action column
          tableColumns.push({
            title: 'Action',
            key: 'action',
            width: 70,
            render: (_, record) => (
              <DeleteButton onClick={() => handleDeleteStudent(record.key)}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </DeleteButton>
            ),
          })

          // Create data rows with proper field mapping
          const tableData = []
          const skippedData = [] // Track skipped records

          // Skip header row (i=0) and process only data rows
          for (let i = 1; i < jsonData.length; i++) {
            // Skip completely empty rows
            if (jsonData[i].every(cell => cell === undefined || cell === '')) {
              continue
            }

            const row = { key: `row-${i}` }

            // Map cells to their respective column names
            headers.forEach((header, index) => {
              row[header] = jsonData[i][index]
            })

            // Check if email is valid
            const email = (row.primary_email || row.email || '')
              .toString()
              .trim()

            if (!isValidEmail(email)) {
              // Add to skipped records with reason
              skippedData.push({
                key: `skipped-${i}`,
                ...row,
                reason: 'Invalid email format',
              })
            } else {
              tableData.push(row)
            }
          }

          // Dedupe valid rows by email (case-insensitive)
          const emailMap = new Map()
          const dedupedTableData = []
          const dupeSkippedData = []

          tableData.forEach(row => {
            const email = (row.primary_email || row.email || '')
              .toString()
              .toLowerCase()
              .trim()

            if (emailMap.has(email)) {
              // This is a duplicate email
              dupeSkippedData.push({
                key: `dupe-${row.key}`,
                ...row,
                reason: 'Duplicate email',
              })
            } else {
              emailMap.set(email, row)
              dedupedTableData.push(row)
            }
          })

          // Set skipped records (both invalid emails and duplicates)
          setSkippedRecords([...skippedData, ...dupeSkippedData])

          // Update state and notify
          setExcelColumns(tableColumns)
          setExcelData(dedupedTableData)

          // User feedback
          const totalSkipped = skippedData.length + dupeSkippedData.length
          const skippedMessage =
            totalSkipped > 0 ? ` (${totalSkipped} records skipped)` : ''

          message.success(
            `File parsed: ${tableData.length} valid rows → ${
              dedupedTableData.length
            } unique ${
              entityType === 'college' ? 'students' : 'candidates'
            }${skippedMessage}.`
          )
        } else {
          message.error('The file is empty or has no data')
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error)
        message.error('Failed to parse the file. Please check the file format.')
      }
    }

    reader.readAsArrayBuffer(file)
  }

  const handleDrag = e => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true)
    } else {
      setIsDragging(false)
    }
  }

  const validateFile = file => {
    if (!file) return false
    if (!ACCEPTED_TYPES.includes(file.type)) {
      message.error('Please upload .xls, .xlsx or .csv file only')
      return false
    }
    if (file.size > MAX_SIZE) {
      message.error('File size should be less than 5MB')
      return false
    }
    return true
  }

  const resetUploadState = () => {
    setFile(null)
    setExcelData([])
    setExcelColumns([])
    setUploadedFile(null)
    setPassingYear(null)
    setSkippedRecords([]);
    localStorage.removeItem('uploadedFile')

    // Reset the file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleFileSelect = e => {
    const selectedFile = e.target.files[0]
    if (validateFile(selectedFile)) {
      // Clear previous state
      resetUploadState()
      // Set new file
      setFile(selectedFile)
      parseExcelFile(selectedFile)
    }
  }

  const handleDrop = e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFile = e.dataTransfer.files[0]
    if (validateFile(droppedFile)) {
      // Clear previous state
      resetUploadState()
      // Set new file
      setFile(droppedFile)
      parseExcelFile(droppedFile)
    }
  }
  useEffect(() => {
    if (!visible) {
      // Reset file input when drawer is closed
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [visible])

  const handleUpload = async () => {
    try {
      // Only validate fields that are visible based on entity type
      if (entityType === 'college') {
        await form.validateFields()
      }

      if (excelData.length === 0) {
        message.error('Please add at least one student')
        return
      }

      const formValues = form.getFieldsValue()
      const formPassingYear = formValues.passingYear

      if (entityType === 'college') {
        if (!formPassingYear) {
          message.error('Please select both start year and passing year')
          return
        }
      }

      setUploading(true)

      try {
        // Only include educational data for college entity type
        let studentData = []

        if (entityType === 'college') {
          // Extract degree, department, and specialization information
          const degreeInfo = selectedDegree
            ? {
                degreeId: selectedDegree.degreeId,
                degreeName: selectedDegree.degreeName,
                degreeShortForm: selectedDegree.degreeShortForm || '',
              }
            : null

          const departmentInfo = selectedDepartment
            ? {
                streamId: selectedDepartment.streamId,
                streamName: selectedDepartment.streamName,
              }
            : null

          const specializationInfo = selectedSpecialization
            ? {
                specialisationId: selectedSpecialization.specialisationId,
                specialisationName: selectedSpecialization.specialisationName,
              }
            : null

          // Map with educational data for college
          const combinedData = excelData.map(row => ({
            first_name: row.first_name || '',
            last_name: row.last_name || '',
            primary_email: row.primary_email || row.email || '',
            // Add qualification information
            degree: degreeInfo,
            department: departmentInfo,
            specialization: specializationInfo,
            // Add year information
            passingYear: formPassingYear,
          }))

          // Dedupe by email (case-insensitive)
          studentData = [
            ...new Map(
              combinedData.map(item => [item.primary_email.toLowerCase(), item])
            ).values(),
          ]
        } else {
          // For corporate, extract basic info and include years
          const combinedData = excelData.map(row => ({
            first_name: row.first_name || '',
            last_name: row.last_name || '',
            primary_email: row.primary_email || row.email || '',
          }))

          // Dedupe by email (case-insensitive)
          studentData = [
            ...new Map(
              combinedData.map(item => [item.primary_email.toLowerCase(), item])
            ).values(),
          ]
        }

        // //console.log(`Final ${entityType} student data:`, studentData)

        // Pass the data back to parent component as a single batch
        onClose(studentData)
        message.success(
          `${studentData.length} unique student${
            studentData.length !== 1 ? 's' : ''
          } uploaded successfully`
        )

        // Reset all state to initial values after successful upload
        resetUploadState()
        form.resetFields()
        setSelectedDegree(null)
        setSelectedDepartment(null)
        setSelectedSpecialization(null)
        setDepartments([])
        setSpecializations([])
        setPassingYear(null) // Reset passing year
      } catch (error) {
        console.error('Error processing data:', error)
        message.error('Failed to process the data')
      } finally {
        setUploading(false)
      }
    } catch (error) {
      console.error('Form validation error:', error)
    }
  }

  const resetManualEntryFields = () => {
    setManualFirstName('')
    setManualLastName('')
    setManualEmail('')
    setManualEntryError('')
  }

  const validateEmail = email => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(String(email).toLowerCase())
  }

  const handleAddManualStudent = () => {
    // Validate inputs
    if (
      !manualFirstName.trim() ||
      !manualEmail.trim()
    ) {
      setManualEntryError('First name and email are required')
      return
    }

    if (!validateEmail(manualEmail)) {
      setManualEntryError('Please enter a valid email address')
      return
    }

    // Normalize input email
    const emailKey = manualEmail.trim().toLowerCase()
    // Check existing
    if (
      excelData.some(r => (r.primary_email || '').toLowerCase() === emailKey)
    ) {
      message.error('This email is already added!')
      return
    }

    // Create a new student record
    const newStudent = {
      key: `manual-${Date.now()}`,
      first_name: manualFirstName.trim(),
      last_name: manualLastName ? manualLastName.trim() : '',
      primary_email: manualEmail.trim(),
      email: manualEmail.trim(), // Include both for compatibility
    }

    // Update table data
    if (excelColumns.length === 0) {
      // First manual entry - create columns
      setExcelColumns([
        { title: 'first_name', dataIndex: 'first_name', key: 'first_name' },
        { title: 'last_name', dataIndex: 'last_name', key: 'last_name' },
        {
          title: 'primary_email',
          dataIndex: 'primary_email',
          key: 'primary_email',
        },
        // Add delete action column for manual entries
        {
          title: 'Action',
          key: 'action',
          width: 70,
          render: (_, record) => (
            <DeleteButton onClick={() => handleDeleteStudent(record.key)}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </DeleteButton>
          ),
        },
      ])
    }

    // Add to table data
    setExcelData(prevData => [...prevData, newStudent])

    // Reset fields
    resetManualEntryFields()

    message.success('Student added successfully')
  }

  // Add function to handle student deletion
  const handleDeleteStudent = key => {
    setExcelData(prevData => prevData.filter(student => student.key !== key))
    message.success('Student removed successfully')
  }

  const getEntityTypeText = () => {
    return entityType === 'college' ? 'Student' : 'Candidate'
  }

  const drawerContent = (
    <Form form={form} layout="vertical" name="bulkUploadForm">
      {entityType === 'college' && (
        <>
          <FormItem
            name="degree"
            label="Degree Name *"
            rules={[{ required: true, message: 'Please select a degree!' }]}
          >
            <StyledAntSelect
              showSearch
              allowClear
              placeholder="Eg. Bachelor of Engineering - B.E"
              options={degrees.map(degree => ({
                label:
                  degree.degreeName +
                  (degree.degreeShortForm
                    ? ` - ${degree.degreeShortForm}`
                    : ''),
                value: degree.degreeId,
              }))}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleDegreeChange}
            />
          </FormItem>

          <FormItem
            name="department"
            label="Department Name *"
            rules={[{ required: true, message: 'Please select a department!' }]}
          >
            <StyledAntSelect
              showSearch
              allowClear
              placeholder="Eg. Computer Science"
              options={departments.map(dept => ({
                label: dept.streamName,
                value: dept.streamId,
              }))}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleDepartmentChange}
              disabled={!selectedDegree}
            />
          </FormItem>

          <FormItem name="specialization" label="Specialization">
            <StyledAntSelect
              showSearch
              allowClear
              placeholder="Select specialization (if any)"
              options={specializations.map(spec => ({
                label: spec.specialisationName,
                value: spec.specialisationId,
              }))}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleSpecializationChange}
              disabled={!selectedDepartment}
            />
          </FormItem>
          <YearSelectContainer>
            <FormItem
              name="passingYear"
              label="Year of Passing *"
              rules={[
                { required: true, message: 'Please select passing year!' },
              ]}
            >
              <StyledAntSelect
                showSearch
                allowClear
                placeholder="Select passing year"
                options={yearOptions}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                onChange={handlePassingYearChange}
              />
            </FormItem>
          </YearSelectContainer>
        </>
      )}

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        padding: '10px 14px',
        background: '#F5F8FF',
        borderRadius: 8,
        border: '1px solid #D6E4FF',
      }}>
        <span style={{ fontSize: 13, color: '#475467' }}>
          Need the bulk upload format?
        </span>
        <a
          href="https://bmv2bqg5gpcd.compat.objectstorage.ap-mumbai-1.oraclecloud.com/pl-uat-public-docs/templates/custom-assessment-bulk-upload-template.xlsx"
          download="custom-assessment-bulk-upload-template.xlsx"
          style={{
            color: '#444CE7',
            fontWeight: 500,
            fontSize: 13,
            textDecoration: 'none',
            marginLeft: 'auto',
          }}
        >
          Download Template
        </a>
      </div>

      <UploadHeading>Upload {getEntityTypeText()} Data</UploadHeading>

      {uploadedFile ? (
        <UploadedFileSection>
          <UploadedFileHeader>Previously Uploaded File</UploadedFileHeader>
          <FileContainer>
            <FileInfo>
              <FileIcon>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 1H12L16 5V19C16 19.5523 15.5523 20 15 20H4C3.44772 20 3 19.5523 3 19V2C3 1.44772 3.44772 1 4 1Z"
                    fill="#207245"
                  />
                  <path d="M12 1V5H16" fill="#207245" />
                  <path
                    d="M6 8H10V9H6V8ZM6 11H10V12H6V11ZM6 14H10V15H6V14Z"
                    fill="white"
                  />
                  <path
                    d="M12 1V5H16"
                    stroke="#207245"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </FileIcon>
              <FileName>{uploadedFile.name}</FileName>
            </FileInfo>
            <RemoveButton
              onClick={e => {
                e.stopPropagation()
                resetUploadState()
              }}
            >
              Remove
            </RemoveButton>
          </FileContainer>
        </UploadedFileSection>
      ) : (
        <>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".xls,.xlsx,.csv"
            style={{ display: 'none' }}
          />
          <UploadArea
            onClick={() => !file && fileInputRef.current?.click()}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            isDragging={isDragging}
            style={{
              background: isDragging ? '#F5F8FF' : '#F9FAFB',
              borderColor: isDragging ? '#444CE7' : '#D0D5DD',
            }}
          >
            <UploadIcon>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 16V8M12 8L9 11M12 8L15 11"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 15V16C3 17.8856 3 18.8284 3.58579 19.4142C4.17157 20 5.11438 20 7 20H17C18.8856 20 19.8284 20 20.4142 19.4142C21 18.8284 21 17.8856 21 16V15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </UploadIcon>
            <UploadText>
              {!file
                ? 'Browse for file or Drag and drop'
                : 'File ready for upload'}
            </UploadText>
            <FormatText>Format (.xls, .xlsx, .csv)</FormatText>
          </UploadArea>

          {file && (
            <FileContainer>
              <FileInfo>
                <FileIcon>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 0H14L20 6V24C20 24.5523 19.5523 25 19 25H4C3.44772 25 3 24.5523 3 24V1C3 0.447715 3.44772 0 4 0Z"
                      fill="#21A366"
                    />
                    <path d="M14 0V6H20" fill="#107C41" />
                    <path
                      d="M8 12H10L11 14L12 12H14L12 16L14 20H12L11 18L10 20H8L10 16L8 12Z"
                      fill="white"
                    />
                    <path
                      d="M14 0V6H20"
                      stroke="#107C41"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </FileIcon>
                <FileName>{file.name}</FileName>
              </FileInfo>
              <RemoveButton
                onClick={e => {
                  e.stopPropagation()
                  resetUploadState()
                }}
              >
                Remove
              </RemoveButton>
            </FileContainer>
          )}

          <AddManuallyButton
            onClick={() => setShowManualEntry(!showManualEntry)}
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M12 4V20M4 12H20"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            {showManualEntry
              ? 'Hide Manual Entry Form'
              : entityType === 'college'
              ? 'Add Student Manually'
              : entityType === 'corporate'
              ? 'Add Candidate Manually'
              : 'Add Manually'}
          </AddManuallyButton>

          {showManualEntry && (
            <ManualEntrySection>
              <ManualEntryGrid>
                <StyledFormItem label="First Name" required>
                  <Input
                    placeholder="Enter first name"
                    value={manualFirstName}
                    onChange={e => setManualFirstName(e.target.value)}
                  />
                </StyledFormItem>
                <StyledFormItem label="Last Name">
                  <Input
                    placeholder="Enter last name"
                    value={manualLastName}
                    onChange={e => setManualLastName(e.target.value)}
                  />
                </StyledFormItem>
                <StyledFormItem label="Email" required>
                  <Input
                    placeholder="Enter email"
                    type="email"
                    value={manualEmail}
                    onChange={e => setManualEmail(e.target.value)}
                  />
                </StyledFormItem>
              </ManualEntryGrid>
              {manualEntryError && (
                <div
                  style={{
                    color: '#ef4444',
                    marginBottom: '8px',
                    fontSize: FONT.F_SM,
                  }}
                >
                  {manualEntryError}
                </div>
              )}
              <AddButton
                onClick={handleAddManualStudent}
                disabled={!manualFirstName || !manualEmail}
              >
                Add Student
              </AddButton>
            </ManualEntrySection>
          )}
        </>
      )}
      {excelData.length > 0 && (
        <>
          <TableTitle>Candidate Data</TableTitle>
          <TableContainer>
            <Table
              columns={excelColumns}
              dataSource={excelData}
              pagination={{ pageSize: 5 }}
              scroll={{ x: true }}
              size="small"
              rowKey="key"
              bordered
            />
          </TableContainer>
        </>
      )}

      {skippedRecords.length > 0 && (
        <>
          <SkippedRecordsTitle>
            <WarningIcon>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 9V13M12 17H12.01M6.41 17.9C4.34 16.62 3 14.38 3 11.86C3 7.86 7.02 4.18 11.2 3.09C15.63 1.93 20 4.73 21.5 9.07C23 13.41 20.58 18.28 16.43 20.24C13.04 21.82 8.84 21.43 6.41 17.9Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </WarningIcon>
            Skipped Records ({skippedRecords.length})
          </SkippedRecordsTitle>
          <SkippedRecordsContainer>
            <Table
              columns={[
                ...(excelColumns.length > 0
                  ? excelColumns.filter(col => col.key !== 'action')
                  : [
                      {
                        title: 'First Name',
                        dataIndex: 'first_name',
                        key: 'first_name',
                      },
                      {
                        title: 'Last Name',
                        dataIndex: 'last_name',
                        key: 'last_name',
                      },
                      {
                        title: 'Email',
                        dataIndex: 'primary_email',
                        key: 'primary_email',
                      },
                    ]),
                {
                  title: 'Reason',
                  dataIndex: 'reason',
                  key: 'reason',
                  render: text => (
                    <span style={{ color: '#b91c1c', fontWeight: '500' }}>
                      {text}
                    </span>
                  ),
                },
              ]}
              dataSource={skippedRecords}
              pagination={{ pageSize: 5 }}
              scroll={{ x: true }}
              size="small"
              rowKey="key"
              bordered
            />
          </SkippedRecordsContainer>
        </>
      )}
    </Form>
  )

  const drawerFooter = (
    <DrawerFooter>
      <Button.Secondary text="Cancel" onClick={onClose} disabled={uploading} />
      <Button.Primary
        text={uploading ? 'Uploading...' : 'Upload'}
        onClick={handleUpload}
        disabled={uploading}
      />
    </DrawerFooter>
  )

  return (
    <Drawer
      title="Bulk Upload"
      placement="right"
      onClose={onClose}
      visible={visible}
      content={drawerContent}
      footer={drawerFooter}
      width="500px"
    />
  )
}

export default BulkUploadDrawer
